import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, LogIn, ShieldAlert, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DATA_OPTIONS = [
  {
    key: "apiaries_and_hives",
    label: "Bijenstanden en kasten",
    detail: "Verwijdert ook gekoppelde inspecties, taken, QR-/scanlogs en adviezen bij deze kasten.",
  },
  {
    key: "inspections",
    label: "Inspecties",
    detail: "Verwijdert inspectieverslagen en bijbehorende afgeleide adviezen.",
  },
  {
    key: "tasks",
    label: "Taken en planning",
    detail: "Verwijdert openstaande, geplande en afgeronde taken.",
  },
  {
    key: "queens_and_breeding",
    label: "Koninginnen en teelt",
    detail: "Verwijdert koninginnenregister, teeltbatches en plaatsingsgeschiedenis.",
  },
  {
    key: "mobile_data",
    label: "Mobiele app-data",
    detail: "Verwijdert mobiele tokens, offline wachtrij, notities, locatie- en scanlogs.",
  },
];

const emptySelection = DATA_OPTIONS.reduce((acc, option) => ({ ...acc, [option.key]: false }), {});

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AccountDeletion() {
  const { user, login, logout, refreshUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [status, setStatus] = useState(null);
  const [selection, setSelection] = useState(emptySelection);
  const [confirmPermanent, setConfirmPermanent] = useState(false);
  const [confirmAccount, setConfirmAccount] = useState(false);
  const [busy, setBusy] = useState(false);

  const selectedCount = useMemo(() => Object.values(selection).filter(Boolean).length, [selection]);

  useEffect(() => {
    if (!user) return;
    setLoadingStatus(true);
    api.get("/auth/account-deletion")
      .then((r) => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoadingStatus(false));
  }, [user]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const result = await login(email, password);
      if (result?.requires_verification) {
        toast.error("Bevestig eerst je e-mailadres via de code die we hebben gestuurd.");
      } else {
        await refreshUser();
        toast.success("Je bent ingelogd.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inloggen is mislukt");
    } finally {
      setLoggingIn(false);
    }
  };

  const deleteSelected = async () => {
    if (selectedCount === 0 || !confirmPermanent) return;
    setBusy(true);
    try {
      await api.post("/auth/data-deletion", selection);
      setSelection(emptySelection);
      setConfirmPermanent(false);
      toast.success("De gekozen gegevens zijn definitief verwijderd.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen is mislukt");
    } finally {
      setBusy(false);
    }
  };

  const requestAccountDeletion = async () => {
    if (!confirmAccount) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/account-deletion/request");
      setStatus(r.data);
      toast.success("Je verwijderingsverzoek is opgeslagen.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verzoek opslaan is mislukt");
    } finally {
      setBusy(false);
    }
  };

  const cancelAccountDeletion = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/account-deletion/cancel");
      setStatus(r.data);
      setConfirmAccount(false);
      toast.success("Het verwijderingsverzoek is geannuleerd.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Annuleren is mislukt");
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account-deletion");
      toast.success("Je account en gegevens zijn verwijderd.");
      logout();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Account verwijderen is mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-zinc-50 text-zinc-950">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12 space-y-6">
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-800">
            Bijen App
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Account en gegevens verwijderen</h1>
          <p className="text-sm leading-6 text-zinc-600">
            Deze pagina hoort bij de Google Play Store-vermelding van Bijen App. Hier kun je inloggen met het account
            dat je in de app gebruikt en daarna een verwijderingsverzoek indienen.
          </p>
        </header>

        <section className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-4">
          <h2 className="font-display text-xl font-bold">Stappen</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <Step number="1" title="Log in" text="Gebruik het e-mailadres en wachtwoord van je Bijen App-account." />
            <Step number="2" title="Kies gegevens" text="Selecteer losse gegevenscategorieen of kies voor je volledige account." />
            <Step number="3" title="Bevestig" text="Selectieve verwijdering is direct permanent. Accountverwijdering heeft 30 dagen wachttijd." />
          </div>
        </section>

        <section className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-3">
          <h2 className="font-display text-xl font-bold">Welke gegevens worden verwijderd?</h2>
          <p className="text-sm leading-6 text-zinc-600">
            Bij selectieve verwijdering worden alleen de aangevinkte categorieen verwijderd. Bij volledige
            accountverwijdering verwijderen we je account, profielgegevens, bijenstanden, kasten, inspecties, taken,
            koninginnen, teeltgegevens, adviezen, mobiele tokens en app-loggegevens die aan jouw gebruiker gekoppeld zijn.
          </p>
          <p className="text-sm leading-6 text-zinc-600">
            Volledige accountverwijdering heeft een bewaarperiode van 30 dagen. Tijdens die periode kun je het verzoek
            annuleren. Na de wachttijd kun je terugkeren naar deze pagina om de definitieve verwijdering te voltooien.
            Selectief verwijderde gegevens hebben geen herstelperiode.
          </p>
        </section>

        {!user ? (
          <section className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                <LogIn className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-xl font-bold">Inloggen om te verwijderen</h2>
                <form onSubmit={submitLogin} className="mt-4 space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="delete-email">E-mailadres</Label>
                    <Input id="delete-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="delete-password">Wachtwoord</Label>
                    <Input id="delete-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12" />
                  </div>
                  <Button type="submit" disabled={loggingIn} className="h-12 bg-amber-600 hover:bg-amber-700">
                    <LogIn className="h-4 w-4" />
                    {loggingIn ? "Inloggen..." : "Inloggen"}
                  </Button>
                </form>
              </div>
            </div>
          </section>
        ) : (
          <section className="space-y-4">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-green-600" />
                <div>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-sm text-zinc-500">{user.email}</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold">Selectieve gegevens verwijderen</h2>
                <p className="mt-1 text-sm text-zinc-600">Deze actie wordt direct uitgevoerd en kan niet ongedaan gemaakt worden.</p>
              </div>
              <div className="space-y-2">
                {DATA_OPTIONS.map((option) => (
                  <label key={option.key} className="flex gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                    <input
                      type="checkbox"
                      checked={selection[option.key]}
                      onChange={(e) => setSelection((prev) => ({ ...prev, [option.key]: e.target.checked }))}
                      className="mt-1 h-5 w-5 accent-amber-600"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-zinc-900">{option.label}</span>
                      <span className="block text-xs leading-5 text-zinc-500">{option.detail}</span>
                    </span>
                  </label>
                ))}
              </div>
              <label className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <input
                  type="checkbox"
                  checked={confirmPermanent}
                  onChange={(e) => setConfirmPermanent(e.target.checked)}
                  className="mt-0.5 h-5 w-5 accent-red-600"
                />
                <span>Ik begrijp dat de aangevinkte gegevens permanent worden verwijderd zonder herstelperiode.</span>
              </label>
              <Button
                type="button"
                disabled={busy || selectedCount === 0 || !confirmPermanent}
                onClick={deleteSelected}
                className="h-12 bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Geselecteerde gegevens verwijderen
              </Button>
            </div>

            <div className="rounded-2xl border border-red-200 bg-white p-5 space-y-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-1 h-5 w-5 shrink-0 text-red-600" />
                <div>
                  <h2 className="font-display text-xl font-bold">Alles verwijderen inclusief account</h2>
                  <p className="mt-1 text-sm leading-6 text-zinc-600">
                    Dit verwijderingsverzoek krijgt een cooldown van 30 dagen. Je account wordt pas na die periode
                    definitief verwijderd.
                  </p>
                </div>
              </div>

              {loadingStatus ? (
                <div className="text-sm text-zinc-500">Status laden...</div>
              ) : status?.requested ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-2 text-amber-900">
                    <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="text-sm">
                      <div className="font-bold">Verwijdering aangevraagd</div>
                      <div>Definitief mogelijk vanaf {formatDate(status.scheduled_for)}.</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" disabled={busy} onClick={cancelAccountDeletion}>
                      <Undo2 className="h-4 w-4" />
                      Verzoek annuleren
                    </Button>
                    <Button type="button" disabled={busy || !status.ready} onClick={deleteAccount} className="bg-red-600 hover:bg-red-700">
                      <Trash2 className="h-4 w-4" />
                      Account definitief verwijderen
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <label className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                    <input
                      type="checkbox"
                      checked={confirmAccount}
                      onChange={(e) => setConfirmAccount(e.target.checked)}
                      className="mt-0.5 h-5 w-5 accent-red-600"
                    />
                    <span>Ik wil mijn volledige Bijen App-account en alle gekoppelde gegevens laten verwijderen.</span>
                  </label>
                  <Button type="button" disabled={busy || !confirmAccount} onClick={requestAccountDeletion} className="h-12 bg-red-600 hover:bg-red-700">
                    <AlertTriangle className="h-4 w-4" />
                    Verwijderingsverzoek indienen
                  </Button>
                </>
              )}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}

function Step({ number, title, text }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900 text-xs font-bold text-white">{number}</div>
      <div className="mt-3 text-sm font-bold text-zinc-900">{title}</div>
      <div className="mt-1 text-xs leading-5 text-zinc-500">{text}</div>
    </div>
  );
}
